import { Store } from '@reduxjs/toolkit';
import { Auth } from 'aws-amplify';
import axios from 'axios';

import config from 'config';

import { StreamingPlatform } from 'types';
import { TOKEN } from 'utils';
import { authActions } from 'store/actions';
import { RootState } from 'store/reducers';

import instances from './instances';
import requests from './requests';

const init = (store: Store<RootState>) => {
  instances.auth.interceptors.request.use(request => {
    const state = store.getState();
    const { currentLiveEventCode } = state.session;

    if (currentLiveEventCode) {
      const { token } = state.events[currentLiveEventCode].session;

      if (token) {
        request.headers.Authorization = `${TOKEN} ${token}`;
      }
    } else {
      const { accessToken } = state.auth;

      if (accessToken) {
        request.headers.Authorization = `${TOKEN} ${accessToken}`;
      }
    }

    return request;
  });

  instances.auth.interceptors.response.use(
    response => response,
    // resource: https://github.com/axios/axios/issues/934#issuecomment-322003342
    error => {
      if (axios.isAxiosError(error)) {
        switch (error.response?.status) {
          case 401: {
            return Auth.currentSession().then(userSession => {
              const accessToken = userSession.getAccessToken().getJwtToken();

              store.dispatch(authActions.setAuth({ accessToken }));

              // eslint-disable-next-line no-param-reassign
              error.config.headers.Authorization = `${TOKEN} ${accessToken}`;

              return axios.request(error.config);
            });
          }
        }
      }

      return Promise.reject(error);
    }
  );

  instances.youtube.interceptors.request.use(request => {
    const state = store.getState();
    const { accessToken } = state.streamingPlatforms[StreamingPlatform.YOUTUBE];

    request.headers.Authorization = `${TOKEN} ${accessToken}`;

    return request;
  });

  instances.youtube.interceptors.response.use(
    response => response,
    error => {
      if (axios.isAxiosError(error)) {
        switch (error.response?.status) {
          case 401: {
            return requests.youtube.refreshToken().then(({ accessToken }) => {
              store.dispatch(authActions.setAuth({ accessToken }));

              // eslint-disable-next-line no-param-reassign
              error.config.headers.Authorization = `${TOKEN} ${accessToken}`;

              return axios.request(error.config);
            });
          }
        }
      }

      return Promise.reject(error);
    }
  );

  instances.facebook.interceptors.request.use(request => {
    const state = store.getState();
    const { accessToken } = state.streamingPlatforms[StreamingPlatform.FACEBOOK];

    request.params = {
      ...request.params,
      access_token: accessToken
    };

    return request;
  });

  instances.twitch.interceptors.request.use(request => {
    const state = store.getState();
    const { accessToken } = state.streamingPlatforms[StreamingPlatform.TWITCH];

    request.headers.Authorization = `${TOKEN} ${accessToken}`;
    request.headers['Client-Id'] = config.oauth.twitch.clientId;

    return request;
  });
};

export default init;
